import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { formatCurrency } from '../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ShoppingCart,
  FileText,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  IndianRupee,
  Package,
  Plus,
  Info,
  AlertTriangle,
  BarChart3,
  ClipboardList
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [monthlySales, setMonthlySales] = useState([]);
  const [insights, setInsights] = useState(null);
  const [versionInfo, setVersionInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, salesRes, insightsRes, versionRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/dashboard/monthly-sales`),
        axios.get(`${API_URL}/dashboard/insights`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/version`).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setMonthlySales(salesRes.data);
      setInsights(insightsRes.data);
      setVersionInfo(versionRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const kpiCards = [
    {
      title: 'Total Purchases',
      subtitle: 'This Month',
      value: formatCurrency(stats?.total_purchases_month || 0),
      icon: ShoppingCart,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Total Sales',
      subtitle: 'This Month',
      value: formatCurrency(stats?.total_sales_month || 0),
      icon: FileText,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600'
    },
    {
      title: 'Total Profit',
      subtitle: 'This Month',
      value: formatCurrency(stats?.total_profit_month || 0),
      icon: TrendingUp,
      iconBg: stats?.total_profit_month >= 0 ? 'bg-teal-100' : 'bg-red-100',
      iconColor: stats?.total_profit_month >= 0 ? 'text-teal-600' : 'text-red-600'
    },
    {
      title: 'Inventory Stock',
      subtitle: 'Total Units',
      value: stats?.total_inventory?.toLocaleString() || '0',
      icon: Package,
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600'
    },
    {
      title: 'GST Payable',
      subtitle: 'Output - Input',
      value: formatCurrency(Math.abs(stats?.gst_payable || 0)),
      icon: IndianRupee,
      iconBg: stats?.gst_payable >= 0 ? 'bg-amber-100' : 'bg-emerald-100',
      iconColor: stats?.gst_payable >= 0 ? 'text-amber-600' : 'text-emerald-600',
      badge: stats?.gst_payable >= 0 ? 'Payable' : 'Credit',
      badgeColor: stats?.gst_payable >= 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
    }
  ];

  const topSellingData = (insights?.top_selling || []).map(p => ({
    name: p.name.length > 18 ? p.name.slice(0, 18) + '...' : p.name,
    fullName: `${p.name} (${p.brand})`,
    qty: p.qty_sold,
    revenue: p.revenue
  }));

  const barColors = ['#14B8A6', '#0EA5E9', '#8B5CF6', '#F59E0B', '#EF4444'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Overview of your business performance</p>
        </div>
        <Button
          onClick={() => navigate('/sales?create=true')}
          className="btn-primary"
          data-testid="dashboard-create-invoice-btn"
        >
          <Plus className="w-4 h-4" />
          Create Invoice
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {kpiCards.map((card, index) => (
          <Card 
            key={index} 
            className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" 
            data-testid={`kpi-card-${index}`}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="kpi-label">{card.title}</p>
                  <p className="kpi-value">{card.value}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-slate-400">{card.subtitle}</span>
                    {card.badge && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${card.badgeColor}`}>
                        {card.badge}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`kpi-card-icon ${card.iconBg}`}>
                  <card.icon className={`w-6 h-6 ${card.iconColor}`} strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Sales Chart */}
        <Card className="lg:col-span-2 border border-slate-200 shadow-sm">
          <CardHeader className="pb-2 border-b border-slate-100">
            <CardTitle className="text-base font-semibold text-slate-900">
              Monthly Sales Trend
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlySales} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#14B8A6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#14B8A6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748B', fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748B', fontSize: 12 }}
                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                    dx={-10}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      padding: '12px'
                    }}
                    formatter={(value) => [formatCurrency(value), 'Sales']}
                    labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke="#14B8A6"
                    strokeWidth={2}
                    fill="url(#salesGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* GST Summary */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-2 border-b border-slate-100">
            <CardTitle className="text-base font-semibold text-slate-900">
              GST Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Input GST</span>
                <ArrowDownRight className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-2xl font-bold text-emerald-700 tabular-nums">
                {formatCurrency(stats?.input_gst || 0)}
              </p>
              <p className="text-xs text-emerald-600 mt-1">Tax Credit Available</p>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl border border-blue-200/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Output GST</span>
                <ArrowUpRight className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-700 tabular-nums">
                {formatCurrency(stats?.output_gst || 0)}
              </p>
              <p className="text-xs text-blue-600 mt-1">Tax Liability</p>
            </div>
            
            <div className={`p-4 rounded-xl border ${stats?.gst_payable >= 0 ? 'bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200/50' : 'bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200/50'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Net Payable</span>
                {stats?.gst_payable >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-amber-600" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-emerald-600" />
                )}
              </div>
              <p className={`text-2xl font-bold tabular-nums ${stats?.gst_payable >= 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                {formatCurrency(Math.abs(stats?.gst_payable || 0))}
              </p>
              <p className={`text-xs mt-1 ${stats?.gst_payable >= 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {stats?.gst_payable >= 0 ? 'Amount to be paid' : 'Credit available'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Insights Row: Top Selling + Low Stock */}
      {insights && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Selling Products */}
          <Card className="border border-slate-200 shadow-sm" data-testid="top-selling-card">
            <CardHeader className="pb-2 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-teal-600" />
                  Top Selling Products
                </CardTitle>
                <Badge variant="outline" className="text-xs bg-teal-50 text-teal-700 border-teal-200">All Time</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {topSellingData.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No sales data yet</p>
                  <p className="text-xs mt-1">Create your first invoice to see top products</p>
                </div>
              ) : (
                <>
                  <div className="h-[200px] mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topSellingData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis
                          dataKey="name"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: '#64748B', fontSize: 10 }}
                          interval={0}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: '#64748B', fontSize: 11 }}
                          allowDecimals={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #E2E8F0',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgb(0 0 0 / 0.08)',
                            padding: '10px',
                            fontSize: '13px'
                          }}
                          formatter={(value, name) => [
                            name === 'qty' ? `${value} units` : formatCurrency(value),
                            name === 'qty' ? 'Qty Sold' : 'Revenue'
                          ]}
                          labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                        />
                        <Bar dataKey="qty" radius={[4, 4, 0, 0]} maxBarSize={40}>
                          {topSellingData.map((_, idx) => (
                            <Cell key={idx} fill={barColors[idx % barColors.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b-0">
                        <TableHead className="text-xs py-2">#</TableHead>
                        <TableHead className="text-xs py-2">Product</TableHead>
                        <TableHead className="text-xs py-2 text-right">Sold</TableHead>
                        <TableHead className="text-xs py-2 text-right">Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(insights.top_selling || []).map((p, i) => (
                        <TableRow key={i} className="border-b-0">
                          <TableCell className="py-2">
                            <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: barColors[i % barColors.length] }}>
                              {i + 1}
                            </div>
                          </TableCell>
                          <TableCell className="py-2">
                            <span className="text-sm font-medium text-slate-800">{p.name}</span>
                            <span className="text-xs text-slate-400 ml-1.5">{p.brand}</span>
                          </TableCell>
                          <TableCell className="py-2 text-right tabular-nums text-sm font-semibold">{p.qty_sold}</TableCell>
                          <TableCell className="py-2 text-right tabular-nums text-sm">{formatCurrency(p.revenue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>

          {/* Low Stock + Quick Actions */}
          <div className="space-y-6">
            {/* Low Stock Alert */}
            <Card className={`border shadow-sm ${(insights.low_stock || []).length > 0 ? 'border-red-200' : 'border-slate-200'}`} data-testid="low-stock-card">
              <CardHeader className="pb-2 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${(insights.low_stock || []).length > 0 ? 'text-red-500' : 'text-slate-400'}`} />
                    Low Stock Items
                  </CardTitle>
                  {(insights.low_stock || []).length > 0 && (
                    <Badge variant="destructive" className="text-xs">{insights.low_stock.length} items</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {(insights.low_stock || []).length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-medium text-emerald-600">All items are well stocked</p>
                    <p className="text-xs text-slate-400 mt-1">No items below 10 units</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(insights.low_stock || []).map((item, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 rounded-lg bg-red-50/60 border border-red-100"
                        data-testid={`low-stock-item-${i}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                            <Package className="w-4 h-4 text-red-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{item.name}</p>
                            <p className="text-xs text-slate-500">
                              {item.brand}{item.size ? ` | ${item.size}` : ''} | Batch: {item.batch_number}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600 tabular-nums">{item.quantity}</p>
                          <p className="text-[10px] text-red-500 font-medium">units left</p>
                        </div>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => navigate('/inventory')}
                    >
                      View All Inventory
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Sales + Pending POs */}
            <Card className="border border-slate-200 shadow-sm" data-testid="recent-activity-card">
              <CardHeader className="pb-2 border-b border-slate-100">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-blue-600" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                {insights.pending_pos > 0 && (
                  <div
                    className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100/60 transition-colors"
                    onClick={() => navigate('/purchase-orders')}
                    data-testid="pending-pos-alert"
                  >
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">Pending Purchase Orders</span>
                    </div>
                    <Badge className="bg-amber-200 text-amber-800 border-0">{insights.pending_pos}</Badge>
                  </div>
                )}
                {(insights.recent_sales || []).length > 0 ? (
                  <div className="space-y-2">
                    {(insights.recent_sales || []).map((sale, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => navigate('/sales')}
                        data-testid={`recent-sale-${i}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <FileText className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{sale.invoice_number}</p>
                            <p className="text-xs text-slate-500">{sale.customer_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-800 tabular-nums">{formatCurrency(sale.grand_total)}</p>
                          <p className="text-[10px] text-slate-400">
                            {sale.created_at ? new Date(sale.created_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">No recent sales</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Version Indicator */}
      {versionInfo && (
        <div className="mt-8 flex justify-center">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-slate-100 rounded-full text-xs text-slate-500" data-testid="version-indicator">
            <Info className="w-3.5 h-3.5" />
            <span>
              <strong>Version {versionInfo.version}</strong>
              <span className="mx-2">|</span>
              Build: {versionInfo.build_date}
              <span className="mx-2">|</span>
              Last Updated: {new Date(versionInfo.last_updated).toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
